// Build grid rows from a parsed file + confirmed mapping, and resolve each row's
// product (by name, with the species the rows agree on), location, length (against the resolved
// product's DB lengths), and the stock a document counted in a unit the import has no code for.

import {
  productById,
  resolveDescriptor,
  resolveLengthValue,
  speciesOf,
} from './catalog';
import { resolveLocationCode } from './locationCatalog';
import {
  BUNDLES,
  UNSTATED,
  packSizeOf,
  stockFor,
  stockSourceOf,
  type StockNote,
  type StockSource,
} from './stockCounts';
import { SELLER_FIELDS, UNIT_CODES } from './targetFields';
import type {
  Candidate,
  ColumnMapping,
  IssueLevel,
  IssueParams,
  ParsedFile,
  ResolutionStatus,
} from './types';

/** One grid row. Target-field values are strings; __-prefixed keys are internal metadata. */
export interface GridRow extends Record<string, unknown> {
  __id: number;
  /** Chosen catalog product id (auto-accepted or user-picked), or '' if unresolved. */
  product: string;
  /** Resolved location code (from the raw locationCode, or user-picked), '' if none. */
  location: string;
  /** Resolved length in inches as a canonical string (matches a product length), or '' if not. */
  lengthInches: string;
  __status: ResolutionStatus;
  __candidates: Candidate[];
  __assumedSpecies?: string;
  __ignoredWords?: string;
  __documentSpecies?: string;
  /** Kept so the stock converts again when the product changes. */
  __stockSource?: StockSource;
  __stockNote?: StockNote;
  /** field key -> issue (level + message key + params), set by validateRows(). */
  __issues: Record<
    string,
    { level: IssueLevel; messageKey: string; params?: IssueParams }
  >;
}

const FIELD_KEYS = SELLER_FIELDS.map((f) => f.key);

/** Resolve the row's length against the currently-resolved product. */
export function resolveRowLength(row: GridRow): void {
  row.lengthInches = resolveLengthValue(row.product, String(row.length ?? ''));
}

export function resolveStock(row: GridRow): void {
  const source = row.__stockSource;
  row.__stockNote = undefined;
  if (!source) return;
  const product = productById(row.product);
  if (!product) {
    if (source.per === UNSTATED) {
      row.unit = '';
      return;
    }
    row.stock = String(source.count);
    row.unit = source.per;
    return;
  }
  const converted = stockFor(
    source.per === BUNDLES
      ? {
          ...source,
          pieces: packSizeOf(String(row.descriptor ?? '')) ?? source.pieces,
        }
      : source,
    product
  );
  row.stock = converted.stock ?? row.stock;
  row.unit = converted.unit;
  row.__stockNote = converted.note;
}

export function resolveRow(row: GridRow): void {
  const resolution = resolveDescriptor(
    String(row.descriptor ?? ''),
    row.__documentSpecies ?? null
  );
  row.__status = resolution.status;
  row.__candidates = resolution.candidates;
  row.product = resolution.chosenId ?? '';
  row.__assumedSpecies = resolution.assumedSpecies ?? '';
  row.__ignoredWords = resolution.ignoredWords?.join(' ') ?? '';
  row.location = resolveLocationCode(String(row.locationCode ?? ''));
  resolveRowLength(row);
  resolveStock(row);
}

// In-place re-resolution of a single edited prop. Callers MUST pass a fresh clone (never a row held
// in React state) — see rebuildRowsWithEdits, which clones each edited row first.
export function reResolveEditedProp(row: GridRow, prop: string): void {
  if (prop === 'product') {
    row.descriptor = String(row.product ?? '');
    resolveRow(row);
  } else if (prop === 'length') {
    resolveRowLength(row);
  } else if (prop === 'stock') {
    forgetStockSource(row);
  } else if (prop === 'location') {
    row.locationCode = String(row.location ?? '');
    row.location = resolveLocationCode(row.locationCode as string);
  } else if (prop === 'unit') {
    // Normalise a pasted unit (e.g. "bf" -> "BF"); clear it if it isn't a known code.
    const raw = String(row.unit ?? '')
      .trim()
      .toUpperCase();
    row.unit = UNIT_CODES.includes(raw) ? raw : '';
    forgetStockSource(row);
  } else if (prop === 'descriptor') {
    resolveRow(row);
  }
}

export function forgetStockSource(row: GridRow): void {
  row.__stockSource = undefined;
  row.__stockNote = undefined;
}

/**
 * Apply grid edits without mutating the rows held in React state. Each edited row (keyed by __id in
 * `editsByRow`) is shallow-cloned, has its edited props re-resolved on the clone, and replaces the
 * original; untouched rows are returned by reference. Returns the same array when there are no edits.
 */
export function rebuildRowsWithEdits(
  rows: GridRow[],
  editsByRow: Map<number, Set<string>>
): GridRow[] {
  if (editsByRow.size === 0) return rows;
  return rows.map((row) => {
    const props = editsByRow.get(row.__id);
    if (!props) return row;
    const clone = { ...row };
    if (props.has('stock') || props.has('unit')) forgetStockSource(clone);
    for (const prop of props) reResolveEditedProp(clone, prop);
    return clone;
  });
}

// A mill list states its species once, if at all, and lists every grade under it.
function speciesAgreedBy(rows: GridRow[]): string | null {
  const speciesByProduct = new Map<string, string>();
  for (const row of rows) {
    const species =
      row.__status === 'auto' ? speciesOf(productById(row.product)) : null;
    if (species) speciesByProduct.set(row.product, species);
  }
  const agreed = new Set(speciesByProduct.values());
  return speciesByProduct.size >= 2 && agreed.size === 1
    ? [...agreed][0]
    : null;
}

export function buildRows(file: ParsedFile, mapping: ColumnMapping): GridRow[] {
  // Invert mapping: targetFieldKey -> sourceHeader.
  const sourceFor: Record<string, string> = {};
  for (const [header, fieldKey] of Object.entries(mapping)) {
    if (fieldKey) sourceFor[fieldKey] = header;
  }

  const rows = file.rows.map((raw, index) => {
    const row: GridRow = {
      __id: index + 1,
      product: '',
      location: '',
      lengthInches: '',
      __status: 'none',
      __candidates: [],
      __issues: {},
    };
    for (const key of FIELD_KEYS) {
      const header = sourceFor[key];
      row[key] = header ? (raw[header] ?? '') : '';
    }
    row.__stockSource = stockSourceOf(row.stock, row.unit, row.descriptor);
    resolveRow(row);
    return row;
  });
  const documentSpecies = speciesAgreedBy(rows);
  if (documentSpecies) {
    rows.forEach((row) => {
      row.__documentSpecies = documentSpecies;
      if (row.__status === 'ambiguous') resolveRow(row);
    });
  }
  return rows;
}
